import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import Button from 'components/Button';
import { createErrorBanner } from 'components/Banner';
import { Card } from 'components/Card';
import { Dropdown, DropdownItem, DropdownMenu } from 'components/Dropdown2';
import { FlyoutContext } from 'components/Flyout/FlyoutContext';
import LoadingDots from 'components/LoadingDots';
import Message from 'components/Message';
import { openModal } from 'components/Modal';
import { createErrorSnackbar, createSuccessSnackbar } from 'components/Snackbar';
import { SvgArrow } from 'components/Svgs';
import Tabs, { Tab, TabLabel } from 'components/Tabs';
import { Title } from 'features/App';
import { bem } from 'helpers/class-name';
import { getApiErrorSnackbarDescription } from 'helpers/api-error-snackbar.helpers';
import { IA_KEY } from 'helpers/ia-logic';
import { makePath } from 'helpers/link-generation';
import Segment from 'helpers/segment';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';
import { selectIsMobile } from 'selectors/ui';
import skuEmptyMenuIcon from 'static/illustrations/sku-empty-menu.svg';

import MobileActionSheet from 'components/MobileActionSheet';
import ModifiersTabPanel from 'features/Sku/Modifiers/dev/ModifiersTabPanel';
import { ADD_MENU_MODAL_NAME, EDIT_MENU_MODAL_NAME } from './components/AddMenuFlyout';
import MenuSelectorDropdown from './components/MenuSelectorDropdown';
import { getMenuId, getMenuName } from './SkuMenuData.normalizers';
import { buildSkuMenuTree } from './SkuMenuOverview.helpers';
import { getSkuCollapseKey } from './SkuItems.constants';
import { createSkuGroup, fetchSkuGroups, updateSkuGroup } from './SkuItems.helpers';
import renderModifiersTab from './ModifiersTab';
import IntegratedPartnerBanner from './components/IntegratedPartnerBanner';
import SkuAddItemForm from './components/SkuAddItemForm';
import SkuEditItemForm from './components/SkuEditItemForm';
import useEditItemDeepLink from './useEditItemDeepLink';
import useSkuCategoryActions from './useSkuCategoryActions';
import useSkuItemActions from './useSkuItemActions';
import useSkuMenuState, { SKU_ITEMS_TAB } from './useSkuMenuState';
import useSkuMutations from './useSkuMutations';
import './SkuItemsBase.scss';
import './SkuMenuPage.scss';
import './components/SkuItemsGroup.scss';
import { SKU_ANALYTICS_EVENTS } from './SkuAnalytics.constants';
import messages from './SkuMenuPage.messages';
import SkuSettingsGearButton from '../shared/SkuSettingsGearButton';
import SkuVenueOpenStatus from '../shared/SkuVenueOpenStatus';
import SkuAllMenusOverview from './components/SkuAllMenusOverview';
import useSkuVenuePermissions from './useSkuVenuePermissions';

const { block, element } = bem('items');

const AddActionsDropdown = ({ attachment, className, onAddItem, onAddCategory, onAddMenu }) => {
  const intl = useIntl();
  const isMobileView = useSelector(selectIsMobile);
  const [isSheetOpen, setIsSheetOpen] = useState(false);

  if (isMobileView) {
    return (
      <div className={className}>
        <button type="button" className={element('add-action')} onClick={() => setIsSheetOpen(true)}>
          <Message message={messages.addMenuActionLabel} />
          <span className={element('menu-title-arrow')}>
            <SvgArrow direction="down" theme="dark" />
          </span>
        </button>
        <MobileActionSheet
          isOpen={isSheetOpen}
          onClose={() => setIsSheetOpen(false)}
          align="center"
          options={[
            {
              label: intl.formatMessage(messages.addItemActionLabel),
              onClick: onAddItem,
            },
            {
              label: intl.formatMessage(messages.addCategoryActionLabel),
              onClick: onAddCategory,
            },
            {
              label: intl.formatMessage(messages.addMenu),
              onClick: onAddMenu,
            },
          ]}
        />
      </div>
    );
  }

  return (
    <Dropdown attachment={attachment} className={className}>
      {({ toggleMenu }) => (
        <>
          <button type="button" className={element('add-action')} onClick={toggleMenu}>
            <Message message={messages.addMenuActionLabel} />
            <span className={element('menu-title-arrow')}>
              <SvgArrow direction="down" theme="dark" />
            </span>
          </button>
          <DropdownMenu>
            <DropdownItem onClick={onAddItem}>
              <Message message={messages.addItemActionLabel} />
            </DropdownItem>
            <DropdownItem onClick={onAddCategory}>
              <Message message={messages.addCategoryActionLabel} />
            </DropdownItem>
            <DropdownItem onClick={onAddMenu}>
              <Message message={messages.addMenu} />
            </DropdownItem>
          </DropdownMenu>
        </>
      )}
    </Dropdown>
  );
};

AddActionsDropdown.propTypes = {
  attachment: PropTypes.string,
  className: PropTypes.string,
  onAddItem: PropTypes.func.isRequired,
  onAddCategory: PropTypes.func.isRequired,
  onAddMenu: PropTypes.func.isRequired,
};

const SkuMenuPage = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const venue = useSelector(selectActiveVenue);
  const { venueId, currencyCode } = venue;
  const { isManual, isIntegrated, integrationName, source } = useSkuVenuePermissions();
  // Temporary employee-only gate for the Modifiers tab pre-rollout; SKU-714 removes this.
  const { featureIsSupported: canSeeModifiers } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_MODIFIERS));
  const flyoutContext = useContext(FlyoutContext);
  const openFlyout = flyoutContext?.openFlyout;
  const shouldRenderAddItemForm = Boolean(flyoutContext);
  const [isSettingsSheetOpen, setIsSettingsSheetOpen] = useState(false);

  const onMenuLoadError = useCallback(() => {
    dispatch(createErrorBanner(messages.menuLoadError));
  }, [dispatch]);
  const onItemsLoadError = useCallback(() => {
    dispatch(createErrorBanner(messages.itemLoadError));
  }, [dispatch]);
  const {
    selectedTab,
    setSelectedTab,
    isLoading,
    menus,
    selectedMenuId,
    setSelectedMenuId,
    categories,
    skuItems,
    refreshTimestampKey,
    refetch,
  } = useSkuMenuState({
    venueId,
    onMenuLoadError,
    onItemsLoadError,
  });

  const { updateMenu } = useSkuMutations({ venueId });

  const {
    handleAddCategory,
    handleEditCategory,
    handleDuplicateCategory,
    handleToggleCategory,
    handleDeleteCategory,
    handleViewCategory,
  } = useSkuCategoryActions({ venueId, menus, selectedMenuId, refetch });

  const pageTitle = isManual ? messages.menuEditor : messages.menu;
  const menuTree = useMemo(() => buildSkuMenuTree({ menus, categories, skuItems }), [menus, categories, skuItems]);
  const selectedMenu = useMemo(
    () => menuTree.find(menu => menu.id === selectedMenuId) || menuTree[0] || null,
    [menuTree, selectedMenuId]
  );
  const isEmptyState = !isLoading && menuTree.length === 0;
  const shouldShowOverview = !isLoading && menuTree.length > 0;
  const isMenuEmpty = shouldShowOverview && (selectedMenu?.categories?.length || 0) === 0;

  const handleCreateMenu = async ({ name, businessHours }) => {
    try {
      const previousMenuIds = new Set(menus.map(getMenuId));
      const response = await createSkuGroup(venueId, {
        name,
        businessHours,
        enabled: true,
      });
      let createdMenuId = getMenuId(response?.group) || getMenuId(response);

      if (!createdMenuId) {
        const updatedMenus = await fetchSkuGroups(venueId);
        const newMenu = updatedMenus.find(menu => !previousMenuIds.has(getMenuId(menu)) && getMenuName(menu) === name);
        createdMenuId = getMenuId(newMenu);
      }

      if (createdMenuId) {
        setSelectedMenuId(createdMenuId);
        Segment.event(SKU_ANALYTICS_EVENTS.MENU_CREATED, { venueId, menuId: createdMenuId, source });
      }
      await refetch();
      dispatch(
        createSuccessSnackbar(
          messages.menuCreateSuccess,
          {
            menuName: name,
          },
          { isLowStyle: true }
        )
      );
    } catch (error) {
      dispatch(
        createErrorSnackbar(
          messages.menuCreateErrorTitle,
          {},
          { isLowStyle: true },
          getApiErrorSnackbarDescription(error)
        )
      );
      throw error;
    }
  };

  const openAddMenu = () => {
    dispatch(
      openModal(ADD_MENU_MODAL_NAME, {
        onSubmit: handleCreateMenu,
      })
    );
  };

  const handleUpdateMenu = ({ name, businessHours, enabled }) => {
    const previousEnabled = selectedMenu?.raw?.enabled ?? true;
    return updateMenu.mutateAsync(
      {
        menuId: selectedMenuId,
        optimistic: { name, businessHours, enabled },
        payload: { name, businessHours, enabled },
      },
      {
        onSuccess: () => {
          Segment.event(SKU_ANALYTICS_EVENTS.MENU_UPDATED, {
            venueId,
            menuId: selectedMenuId,
            source,
          });
          if (enabled !== previousEnabled) {
            Segment.event(SKU_ANALYTICS_EVENTS.MENU_AVAILABILITY_CHANGED, {
              venueId,
              menuId: selectedMenuId,
              enabled,
              source,
            });
          }
          dispatch(createSuccessSnackbar(messages.menuUpdateSuccess, { menuName: name }, { isLowStyle: true }));
        },
        onError: error => {
          dispatch(
            createErrorSnackbar(
              messages.menuUpdateErrorTitle,
              {},
              { isLowStyle: true },
              getApiErrorSnackbarDescription(error)
            )
          );
        },
      }
    );
  };

  const navigate = useNavigate();

  const openEditMenu = () => {
    dispatch(
      openModal(EDIT_MENU_MODAL_NAME, {
        onSubmit: handleUpdateMenu,
        initialData: {
          name: selectedMenu?.name,
          businessHours: selectedMenu?.raw?.businessHours,
          enabled: selectedMenu?.raw?.enabled ?? true,
        },
      })
    );
  };

  const isMenuEnabled = selectedMenu?.raw?.enabled !== false;

  const handleToggleMenu = async () => {
    const newEnabled = !isMenuEnabled;
    try {
      await updateSkuGroup(venueId, selectedMenuId, {
        name: selectedMenu?.name,
        businessHours: selectedMenu?.raw?.businessHours,
        enabled: newEnabled,
      });
      await refetch();
      dispatch(
        createSuccessSnackbar(messages.menuUpdateSuccess, { menuName: selectedMenu?.name }, { isLowStyle: true })
      );
    } catch (error) {
      dispatch(
        createErrorSnackbar(
          messages.menuUpdateErrorTitle,
          {},
          { isLowStyle: true },
          getApiErrorSnackbarDescription(error)
        )
      );
    }
  };

  const handleAddClosedDates = useCallback(() => {
    navigate(makePath.editVenueProfile(venueId));
  }, [navigate, venueId]);

  const handleEditBusinessProfile = useCallback(() => {
    navigate(makePath.editVenuePhotos(venueId));
  }, [navigate, venueId]);

  const {
    preselectedCategoryIds,
    selectedItemForEdit,
    preselectedEditCategoryIds,
    handleAddItemClick,
    handleAddItemSuccess,
    handleEditItemClick,
    handleEditItemSuccess,
    handleDeleteItem,
    handleStatusChange,
  } = useSkuItemActions({
    venueId,
    refetch,
    openFlyout,
  });

  useEditItemDeepLink({ skuItems, handleEditItemClick });

  useEffect(() => {
    document.body.classList.add('sku-items-page-active');
    return () => {
      document.body.classList.remove('sku-items-page-active');
    };
  }, []);

  return (
    <div className={`${block()} sku-items-page`}>
      <Title>
        <Message message={pageTitle} />
      </Title>
      <Tabs selectedTab={selectedTab} onTabChange={setSelectedTab}>
        <Tab name={SKU_ITEMS_TAB}>
          <TabLabel>
            <Message message={messages.itemsTab} />
          </TabLabel>
          <div className={element('venue-status-row')}>
            <SkuVenueOpenStatus />
          </div>
          {isIntegrated && <IntegratedPartnerBanner integrationName={integrationName} />}
          {isLoading && <LoadingDots />}
          {isEmptyState && (
            <Card className={element('stretch-card', 'card--no-border')}>
              <div className={element('empty-state-content')}>
                <img className={element('empty-state-icon')} src={skuEmptyMenuIcon} alt="" aria-hidden />
                <div className={element('empty-state-message')}>
                  <Message message={messages.noMenusTitle} />
                </div>
                <div className={element('empty-state-submessage')}>
                  <Message message={messages.noMenusSubtitle} />
                </div>
                {isManual && (
                  <Message
                    as={Button}
                    className={element('empty-state-action')}
                    message={messages.addMenu}
                    onClick={openAddMenu}
                    styled="primary"
                    type={Button.TYPES.BUTTON}
                  />
                )}
              </div>
            </Card>
          )}
          {shouldShowOverview && (
            <>
              <div className={element('menu-selector')}>
                <div className={element('menu-header')}>
                  <MenuSelectorDropdown
                    menus={menuTree}
                    selectedId={selectedMenu?.id}
                    onChange={setSelectedMenuId}
                    onAddMenu={isManual ? openAddMenu : undefined}
                  />
                  <div className={element('menu-actions')}>
                    {isManual && (
                      <AddActionsDropdown
                        attachment="right"
                        onAddItem={() => handleAddItemClick()}
                        onAddCategory={handleAddCategory}
                        onAddMenu={openAddMenu}
                      />
                    )}
                    {isMobile ? (
                      <>
                        <SkuSettingsGearButton
                          ariaLabel={intl.formatMessage(messages.menuSettingsAriaLabel)}
                          onClick={() => setIsSettingsSheetOpen(true)}
                        />
                        <MobileActionSheet
                          isOpen={isSettingsSheetOpen}
                          onClose={() => setIsSettingsSheetOpen(false)}
                          options={[
                            [
                              ...(isManual
                                ? [
                                    {
                                      label: intl.formatMessage(messages.editMenu),
                                      onClick: openEditMenu,
                                    },
                                  ]
                                : []),
                              {
                                label: intl.formatMessage(isManual ? messages.closedDates : messages.viewClosedDates),
                                onClick: handleAddClosedDates,
                              },
                              {
                                label: intl.formatMessage(
                                  isManual ? messages.businessProfile : messages.viewBusinessProfile
                                ),
                                onClick: handleEditBusinessProfile,
                              },
                            ],
                            isManual
                              ? [
                                  {
                                    label: intl.formatMessage(isMenuEnabled ? messages.hideMenu : messages.showMenu),
                                    onClick: handleToggleMenu,
                                  },
                                ]
                              : [],
                          ].filter(group => group.length > 0)}
                        />
                      </>
                    ) : (
                      <Dropdown attachment="right">
                        {({ toggleMenu }) => (
                          <>
                            <SkuSettingsGearButton
                              ariaLabel={intl.formatMessage(messages.menuSettingsAriaLabel)}
                              onClick={toggleMenu}
                            />
                            <DropdownMenu>
                              {isManual && (
                                <DropdownItem onClick={openEditMenu}>
                                  <Message message={messages.editMenu} />
                                </DropdownItem>
                              )}
                              <DropdownItem onClick={handleAddClosedDates}>
                                <Message message={isManual ? messages.closedDates : messages.viewClosedDates} />
                              </DropdownItem>
                              <DropdownItem onClick={handleEditBusinessProfile}>
                                <Message message={isManual ? messages.businessProfile : messages.viewBusinessProfile} />
                              </DropdownItem>
                            </DropdownMenu>
                          </>
                        )}
                      </Dropdown>
                    )}
                  </div>
                </div>
              </div>
              {isMenuEmpty ? (
                <Card className={element('stretch-card', 'card--no-border')}>
                  <div className={element('empty-state-content')}>
                    <img className={element('empty-state-icon')} src={skuEmptyMenuIcon} alt="" aria-hidden />
                    <div className={element('empty-state-message')}>
                      <Message message={messages.menuEmptyTitle} />
                    </div>
                    <div className={element('empty-state-submessage')}>
                      <Message message={messages.menuEmptySubtitle} />
                    </div>
                    {isManual && (
                      <AddActionsDropdown
                        attachment="center"
                        className={element('empty-state-action')}
                        onAddItem={() => handleAddItemClick()}
                        onAddCategory={handleAddCategory}
                        onAddMenu={openAddMenu}
                      />
                    )}
                  </div>
                </Card>
              ) : (
                <SkuAllMenusOverview
                  menus={selectedMenu ? [selectedMenu] : []}
                  noCategoriesMessage={messages.noCategories}
                  hideMenuTitle
                  currencyCode={currencyCode}
                  collapseStorageKey={getSkuCollapseKey(venueId)}
                  onAddItem={isManual ? handleAddItemClick : undefined}
                  onEditItem={isManual ? handleEditItemClick : undefined}
                  onDeleteItem={isManual ? handleDeleteItem : undefined}
                  onEditCategory={isManual ? handleEditCategory : undefined}
                  onDuplicateCategory={isManual ? handleDuplicateCategory : undefined}
                  onToggleCategory={isManual ? handleToggleCategory : undefined}
                  onDeleteCategory={isManual ? handleDeleteCategory : undefined}
                  onStatusChange={isManual ? handleStatusChange : undefined}
                  onViewItem={isIntegrated ? handleEditItemClick : undefined}
                  onViewCategory={isIntegrated ? handleViewCategory : undefined}
                />
              )}
            </>
          )}
        </Tab>
        {canSeeModifiers && renderModifiersTab({ DevPanel: ModifiersTabPanel, venueId })}
      </Tabs>

      {shouldRenderAddItemForm && (
        <>
          <SkuAddItemForm
            currencyCode={currencyCode}
            preselectedCategoryIds={preselectedCategoryIds}
            categoriesRefreshKey={refreshTimestampKey}
            onSuccess={handleAddItemSuccess}
          />
          <SkuEditItemForm
            currencyCode={currencyCode}
            item={selectedItemForEdit}
            preselectedCategoryIds={preselectedEditCategoryIds}
            categoriesRefreshKey={refreshTimestampKey}
            onSuccess={handleEditItemSuccess}
            isReadOnly={isIntegrated}
          />
        </>
      )}
    </div>
  );
};

export default SkuMenuPage;
